import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack
} from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useParams } from 'react-router';

import LoadingIndicator from '@/components/LoadingIndicator';
import VulnerabilityDetailsAssetTable from '@/features/VulnerabilityDetailsAssetTable';
import VulnerabilityDetailsInfo from '@/features/VulnerabilityDetailsInfo';
import VulnerabilityDetailsMainInformation from '@/features/VulnerabilityDetailsMainInformation';
import VulnerabilityDetailsSubTopBar from '@/features/VulnerabilityDetailsSubTopBar';
import MainContent from '@/layout/MainContent';
import isLayoutMobileAtom from '@/store/layout';
import { isSidebarExpandedAtom } from '@/store/sidebar';

import { useFetchVulnerabilityOrRemediation } from '../VMPVulnerabilities/VMPVulnerabilities.store';
import {
  vmpAffectedAssetsTableRowsAtom,
  vmpRemediatedAssetsTableRowsAtom
} from '../VulnerabilityManagement.store';
import useStyles from './VulnerabilityDetails.styles';

const VulnerabilityDetails = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const { classes } = useStyles({ isLayoutMobile });
  const [isSidebarExpanded] = useAtom(isSidebarExpandedAtom);
  const { id } = useParams();
  const { isLoading } = useFetchVulnerabilityOrRemediation(id);

  const [affectedRowsPerPage, setAffectedRowsPerPage] = useAtom(
    vmpAffectedAssetsTableRowsAtom
  );

  const [remediatedRowsPerPage, setRemediatedRowsPerPage] = useAtom(
    vmpRemediatedAssetsTableRowsAtom
  );

  const width = useMemo(() => {
    // mobile
    if (isLayoutMobile) return '100%';
    // desktop
    if (isSidebarExpanded) return 'calc(100vw - 21.5rem)';
    return 'calc(100vw - 7rem)';
  }, [isLayoutMobile, isSidebarExpanded]);

  if (isLoading) {
    return <LoadingIndicator isLoading={isLoading} />;
  }

  return (
    <>
      <VulnerabilityDetailsSubTopBar />
      <MainContent>
        <Box
          sx={{
            height: 'auto',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div className={classes.vulnerabilityDetails}>
            <div className={classes.vulnerabilityDetailsColumnLeft}>
              <VulnerabilityDetailsMainInformation />
            </div>
            <Stack
              className={classes.vulnerabilityDetailsColumnRight}
              direction="column"
              sx={{ gap: '1.5rem' }}
            >
              <div className={classes.vulnerabilityDetailsColumnRightItem}>
                <VulnerabilityDetailsInfo />
              </div>
            </Stack>
          </div>
          <Divider sx={{ mb: 3, mt: 1 }} />
          <Card sx={{ mb: 4, width }}>
            <CardHeader
              className={classes.cardHeader}
              title="Affected Assets"
              slotProps={{ title: { variant: 'h4' } }}
            />
            <CardContent sx={{ padding: '0 !important', overflow: 'auto' }}>
              <VulnerabilityDetailsAssetTable
                rowsPerPage={affectedRowsPerPage}
                setRowsPerPage={setAffectedRowsPerPage}
              />
            </CardContent>
          </Card>
          <Card sx={{ width }}>
            <CardHeader
              className={classes.cardHeader}
              title="Remediated Assets"
              slotProps={{ title: { variant: 'h4' } }}
            />
            <CardContent sx={{ padding: '0 !important', overflow: 'auto' }}>
              <VulnerabilityDetailsAssetTable
                rowsPerPage={remediatedRowsPerPage}
                setRowsPerPage={setRemediatedRowsPerPage}
                remediated
              />
            </CardContent>
          </Card>
        </Box>
      </MainContent>
    </>
  );
};

export default VulnerabilityDetails;
